import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';

/**
 * Helper zum Setzen von Klassen basierend auf CSS Selektoren
 *
 * Setzt Klasse `key` wenn der Selector in `value` ein Ergebnis liefert.
 * Beginnt `key` mit `!`, dann wird das Ergebnis negiert.
 *
 * @usageNotes
 * ```
 *     <some-element [appClassSelector]="{'first': 'img', '!second': 'input.form-control:active'}">...</some-element>
 *
 *     <some-element [appClassSelector]="objExp">...</some-element>
 *
 * ```
 *
 */
@Directive({
	selector: '[appClassSelector]',
})
export class ClassSelectorDirective implements OnInit, OnDestroy, OnChanges {

	@Input()
	appClassSelector: { [key: string]: string };
	observer: MutationObserver;

	constructor(private element: ElementRef<HTMLElement>) {
		this.observer = new MutationObserver(() => this.update());
	}

	ngOnInit(): void {
		this.update();
		this.enable();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if ('appClassSelector' in changes) {
			this.update();
		}
	}

	ngOnDestroy(): void {
		this.disable();
	}

	private enable(): void {
		this.observer.observe(this.element.nativeElement, {subtree: true, childList: true, attributes: true});
	}

	private disable(): void {
		this.observer.disconnect();
	}


	update(): void {
		if (!this.appClassSelector) {
			return;
		}

		const result = {};
		const remove = new Array<string>();
		const add = new Array<string>();
		for (const key in this.appClassSelector) {
			const negate = key.startsWith('!');
			const name = negate ? key.substring(1) : key;
			result[name] = !!this.element.nativeElement.querySelector(this.appClassSelector[key]) !== negate;
			if (result[name]) {
				add.push(name);
			} else {
				remove.push(name);
			}
		}
		this.disable();
		this.element.nativeElement.classList.add(...add);
		this.element.nativeElement.classList.remove(...remove);
		this.enable();
	}
}
